import * as Sentry from "@sentry/react";

/**
 * URL FRAGMENTS ARE SCRUBBED ON THE WAY OUT.
 *
 * A signing link — a witness's, or a deposit payer's — carries its 256-bit token in
 * `location.hash`, which browsers never put on the wire. An error reporter is not a
 * browser: it reads `location.href` itself and would ship that token to Sentry as an event
 * URL, a transaction name or a navigation breadcrumb — turning a credential the network
 * never saw into telemetry.
 *
 * Every string that can carry a URL is therefore truncated at the first `#`. The rule is
 * deliberately GENERIC rather than a check for one signing route, so a fragment secret
 * added anywhere later is covered by this same pass and not by a new one. It is also what
 * covers the `/sign/witness` → `/sign/link` redirect, whose whole point is to carry a
 * fragment from one route to another.
 */
function withoutFragment(value: string): string {
  const hash = value.indexOf("#");
  return hash === -1 ? value : value.slice(0, hash);
}

function scrubUrlKeys(data: Record<string, unknown> | undefined) {
  if (!data) return;
  // `url` on a fetch/xhr crumb; `from`/`to` on a navigation crumb.
  for (const key of ["url", "from", "to"]) {
    const value = data[key];
    if (typeof value === "string") data[key] = withoutFragment(value);
  }
}

function scrubFragments<T extends Sentry.Event>(event: T): T {
  if (event.request?.url) event.request.url = withoutFragment(event.request.url);
  if (event.transaction) event.transaction = withoutFragment(event.transaction);

  for (const crumb of event.breadcrumbs ?? []) {
    scrubUrlKeys(crumb.data);
  }

  scrubUrlKeys(event.contexts?.trace?.data);

  return event;
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  sampleRate: 1.0,
  tracesSampleRate: 0.1,
  environment: import.meta.env.VITE_ENVIRONMENT ?? import.meta.env.MODE,
  // Errors and performance events are two separate pipelines; a scrub on one only is a
  // scrub that leaks through the other.
  beforeSend: (event) => scrubFragments(event),
  beforeSendTransaction: (event) => scrubFragments(event),
});

export { Sentry };
